import type { IMetaParams, IMetaTemplate, MetaHeaderType } from './meta-wpp-template.service';
import type { ResponseTemplate } from '../templates/templates.service';
import { parseButtonsComponent, MetaButtonVariants, type MetaButtonKind } from './template-buttons';
import { parseComponentJson } from './meta-template-params';
import { seloDaCategoria } from './template-categories';

/**
 * Modelo e renderização da pré-visualização de um template Meta.
 *
 * Existe porque a MESMA bolha precisa aparecer em dois runtimes diferentes: o
 * editor da ação, que monta `<MetaTemplatePreview>`, e o card do nó no editor de
 * bots, que o Drawflow exige como **string de HTML** — ele insere `innerHTML` e
 * não instancia componentes. Sem uma fonte única, o operador compararia duas
 * bolhas que divergiriam na primeira mudança de layout, justamente onde ele
 * confia no que está vendo.
 *
 * O módulo é puro: nada de Vue, nada de rede. Quem chama já tem o template.
 */

/** Botão do template, já classificado quanto a devolver ou não resposta ao fluxo. */
export interface BotaoPreview {
    /** Posição no `buttonsJson`, base 0. É a chave de `BotAcaoTransicao.valor`. */
    indice: number;
    texto: string;
    tipo: MetaButtonKind;
    /**
     * `true` só para `QUICK_REPLY`.
     *
     * Os demais agem no aparelho do contato — abrem um link, discam, copiam um
     * código — e **não** geram webhook de volta. Dar saída conectável a eles
     * desenharia no fluxograma um caminho que nunca seria percorrido.
     */
    respondivel: boolean;
    icon: string;
}

export interface PreviewTemplate {
    /**
     * De qual catálogo o template veio.
     *
     * Decide COMO as variáveis do texto são marcadas: o template Meta tem
     * `{{parametro}}` preenchido pelo operador na própria ação, e o do Whiz tem
     * `{nome_contato}` resolvido pelo servidor no envio. Marcar os dois igual diria
     * ao operador que ele esqueceu de preencher algo que não é dele preencher.
     */
    origem: 'META' | 'WHIZ';
    /** `null` quando o template não tem cabeçalho. */
    headerType: 'text' | 'file' | null;
    headerText: string;
    headerFileMime?: string;
    headerFileUrl: string;
    headerFileName: string;
    bodyText: string;
    footerText: string;
    botoes: BotaoPreview[];
    categoria?: string | null;
}

const ICONE_POR_VARIANTE: Record<string, string> = Object.fromEntries(
    MetaButtonVariants.map(v => [v.kind as string, v.icon]),
);

/** Cabeçalhos de mídia, mapeados para o que o desenho espera em `headerFileMime`. */
const MIME_POR_HEADER: Partial<Record<MetaHeaderType, string>> = {
    MEDIA_PNG_JPG_JPEG: 'JPG',
    MEDIA_MP4: 'MP4',
    MEDIA_PDF: 'PDF',
};

/**
 * URL do arquivo do cabeçalho.
 *
 * `Storage.path` é a cópia re-hospedada e vem primeiro; `s3FileAddress` é o
 * endereço direto, usado em template importado, que não tem Storage nosso. Sem
 * nenhum dos dois não há o que desenhar, e a bolha cai no cartão de arquivo.
 */
const urlDoCabecalho = (template?: IMetaTemplate | null): string =>
    template?.metaUpload?.Storage?.path || template?.metaUpload?.s3FileAddress || '';

const ehCabecalhoDeMidia = (headerType?: MetaHeaderType | null) =>
    !!headerType && headerType.startsWith('MEDIA_');

/** Botões do template, na ordem em que a Meta os entrega. */
export function botoesDoTemplate(template?: IMetaTemplate | null): BotaoPreview[] {
    if (!template) return [];

    return parseButtonsComponent(template.buttonsJson).map((botao, indice) => ({
        indice,
        // `COPY_CODE` não tem `text` — o rótulo que o WhatsApp desenha é fixo.
        texto: (botao as { text?: string }).text || (botao.type === 'COPY_CODE' ? 'Copiar código' : ''),
        tipo: botao.type as MetaButtonKind,
        respondivel: botao.type === 'QUICK_REPLY',
        icon: ICONE_POR_VARIANTE[botao.type] ?? 'ri-checkbox-blank-circle-line',
    }));
}

/** Só os botões que devolvem resposta — os que viram saída conectável no fluxo. */
export const botoesRespondiveis = (botoes: BotaoPreview[]) => botoes.filter(b => b.respondivel);

/**
 * Modelo da bolha a partir do template gravado.
 *
 * Os textos saem CRUS, com os `{{...}}` no lugar: a interpolação é um passo à
 * parte (`interpolarComDestaque`) porque quem desenha precisa distinguir o que é
 * literal do que é variável — e essa distinção se perde numa string já trocada.
 */
export function previewDoTemplate(template?: IMetaTemplate | null): PreviewTemplate {
    const header = parseComponentJson(template?.headerJson);
    const body = parseComponentJson(template?.bodyJson);
    const footer = parseComponentJson(template?.footerJson);

    const midia = ehCabecalhoDeMidia(template?.headerType);

    return {
        origem: 'META',
        headerType: !template?.headerType ? null : (midia ? 'file' : 'text'),
        headerText: (header?.text as string) ?? '',
        headerFileMime: midia ? MIME_POR_HEADER[template!.headerType!] : undefined,
        headerFileUrl: urlDoCabecalho(template),
        headerFileName: template?.metaUpload?.Storage?.filename || template?.metaUpload?.originalFileName || '',
        bodyText: (body?.text as string) ?? '',
        footerText: (footer?.text as string) ?? '',
        botoes: botoesDoTemplate(template),
        categoria: template?.category ?? null,
    };
}

/**
 * Variáveis do bot, de chave SIMPLES, resolvidas pelo servidor no envio.
 *
 * Lista fechada e espelhada de `substituirVariaveis` no server. Fechada de
 * propósito: `{qualquer coisa}` entre chaves num template é texto literal, e
 * destacá-lo como variável prometeria uma troca que nunca vai acontecer.
 */
const VARIAVEIS_DO_BOT = ['nome_contato', 'nome_whatsapp', 'numero_protocolo', 'nome_canal', 'codigo'] as const;

const VARIAVEL_DO_BOT = new RegExp(`\\{(${VARIAVEIS_DO_BOT.join('|')})\\}`, 'g');

/** Extensões que o cabeçalho de um template do Whiz pode ter, para escolher como desenhá-lo. */
function mimeDeAnexo(url: string): string {
    const extensao = (url.split('?')[0].split('.').pop() ?? '').toLowerCase();
    if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(extensao)) return 'JPG';
    if (['mp4', 'webm', 'mov'].includes(extensao)) return 'MP4';
    return 'PDF';
}

/**
 * Modelo da bolha de um template do WHIZ.
 *
 * Os quatro conteúdos viram formas bem diferentes, e o campo `mensagem` muda de
 * significado em cada um (texto, URL do anexo, nome da conta Pix) — é a razão de a
 * tradução morar aqui, e não espalhada em quem desenha.
 *
 * Pix e Senha de uso único desenham botão, mas nenhum deles é respondível: o
 * contato copia um código, não devolve uma escolha ao fluxo.
 */
export function previewDoTemplateWhiz(template?: ResponseTemplate | null): PreviewTemplate {
    const base: PreviewTemplate = {
        origem: 'WHIZ',
        headerType: null,
        headerText: '',
        headerFileUrl: '',
        headerFileName: '',
        bodyText: '',
        footerText: '',
        botoes: [],
        categoria: null,
    };

    if (!template) return base;

    switch (template.conteudo) {
        case 'TEXTO':
            return { ...base, bodyText: template.mensagem };

        case 'ANEXO':
            return {
                ...base,
                headerType: 'file',
                headerFileMime: mimeDeAnexo(template.mensagem),
                headerFileUrl: template.mensagem,
                headerFileName: decodeURIComponent((template.mensagem.split('?')[0].split('/').pop() ?? 'Arquivo')),
                // Anexo de áudio não tem legenda — a bolha fica só com o arquivo.
                bodyText: template.legenda ?? '',
            };

        case 'SENHA_USO_UNICO':
            return {
                ...base,
                headerType: 'text',
                headerText: 'Senha de uso único',
                bodyText: template.mensagem,
                botoes: [{
                    indice: 0,
                    texto: template.textoBotao || 'Copiar código',
                    tipo: 'COPY_CODE',
                    respondivel: false,
                    icon: 'ri-file-copy-line',
                }],
            };

        case 'PIX':
            return {
                ...base,
                // Em PIX o campo `mensagem` é o NOME DA CONTA, não um texto de mensagem.
                bodyText: template.nomeContaPix || template.mensagem || '',
                footerText: template.chavePix ? `Chave: ${template.chavePix}` : '',
                botoes: [{
                    indice: 0,
                    texto: 'Copiar chave Pix',
                    tipo: 'COPY_CODE',
                    respondivel: false,
                    icon: 'ri-qr-code-line',
                }],
            };
    }

    return base;
}

/**
 * Marca as variáveis do bot no texto, sem trocá-las por valor.
 *
 * Não há valor para trocar: quem resolve `{nome_contato}` é o servidor, no envio,
 * com o contato real. O destaque aqui diz "isto vira outra coisa na entrega" — e é
 * um estado DIFERENTE do `{{parametro}}` vazio de um template Meta, que é um
 * campo que o operador deixou de preencher e faz a Meta recusar o envio.
 */
export function destacarVariaveisDoBot(texto: string | null | undefined): string {
    const conteudo = texto ?? '';
    if (!conteudo) return '';

    let resultado = '';
    let ultimo = 0;

    for (const casamento of conteudo.matchAll(VARIAVEL_DO_BOT)) {
        const inicio = casamento.index ?? 0;
        resultado += escaparHtml(conteudo.slice(ultimo, inicio));
        resultado += `<span class="tpl-var tpl-var--auto" title="Preenchido pelo sistema no envio.">${escaparHtml(casamento[0])}</span>`;
        ultimo = inicio + casamento[0].length;
    }

    return resultado + escaparHtml(conteudo.slice(ultimo));
}

const PLACEHOLDER = /\{\{\s*([^{}]+?)\s*\}\}/g;

/** Valores informados, por chave — nome para templates NOMEADOS, posição (1-based) para POSICIONAIS. */
function valoresPorChave(lista: unknown): Record<string, string> {
    const mapa: Record<string, string> = {};
    if (!Array.isArray(lista)) return mapa;

    lista.forEach((item, indice) => {
        if (Array.isArray(item)) mapa[String(item[0])] = String(item[1] ?? '');
        else if (typeof item === 'string') mapa[String(indice + 1)] = item;
    });
    return mapa;
}

export function escaparHtml(texto: string): string {
    return texto
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
}

/**
 * Troca os `{{...}}` pelos valores preenchidos, marcando cada troca.
 *
 * Devolve **HTML**, não texto: o valor entra num `<span class="tpl-var">` para
 * ser lido como dinâmico sem o operador precisar decorar quais trechos eram
 * variáveis. Placeholder sem valor vira `tpl-var--vazia` com o nome à mostra —
 * inventar um valor ali mentiria sobre o que a Meta vai receber, e esconder o
 * buraco é pior: é exatamente o estado que faz a Meta recusar o envio (132000).
 *
 * Tudo que vem do template é escapado antes de concatenar; o retorno é inserido
 * via `innerHTML` no card do nó.
 */
export function interpolarComDestaque(
    texto: string | null | undefined,
    params: IMetaParams | null | undefined,
    secao: 'header' | 'body',
): string {
    const conteudo = texto ?? '';
    if (!conteudo) return '';

    const valores = params && !('replace' in params)
        ? valoresPorChave(secao === 'header' ? params.header : params.body)
        : {};

    let resultado = '';
    let ultimo = 0;

    for (const casamento of conteudo.matchAll(PLACEHOLDER)) {
        const inicio = casamento.index ?? 0;
        resultado += escaparHtml(conteudo.slice(ultimo, inicio));

        const chave = (casamento[1] ?? '').trim();
        const valor = valores[chave];

        resultado += valor
            ? `<span class="tpl-var">${escaparHtml(valor)}</span>`
            : `<span class="tpl-var tpl-var--vazia" title="Variável sem valor — a Meta recusa o envio assim.">${escaparHtml(chave)}</span>`;

        ultimo = inicio + casamento[0].length;
    }

    return resultado + escaparHtml(conteudo.slice(ultimo));
}

/** `true` quando ainda há `{{...}}` sem valor — o que trava o salvamento da ação. */
export function temVariavelSemValor(
    preview: PreviewTemplate,
    params: IMetaParams | null | undefined,
): boolean {
    // Template do Whiz não tem parâmetro a preencher — as variáveis dele são
    // resolvidas no envio, então nunca há o que faltar.
    if (preview.origem === 'WHIZ') return false;

    const marca = 'tpl-var--vazia';
    return interpolarComDestaque(preview.headerText, params, 'header').includes(marca)
        || interpolarComDestaque(preview.bodyText, params, 'body').includes(marca);
}

/**
 * A bolha como string de HTML, para o card do nó.
 *
 * Espelha a marcação de `MetaTemplatePreview.vue`. As classes são as mesmas, e os
 * estilos vivem no `<style>` do editor de bots — ali o CSS não é `scoped` para
 * este trecho justamente porque o HTML nasce fora da árvore do componente.
 *
 * `comPortas` marca que este desenho é o do CARD: os botões viram linhas de altura
 * fixa, na mesma ordem das portas, e o editor reposiciona cada porta na altura da
 * linha correspondente (`alinharPortasDoTemplate`).
 */
export function renderPreviewTemplateHTML(
    preview: PreviewTemplate,
    params: IMetaParams | null | undefined,
    opcoes: { comPortas?: boolean; semSelo?: boolean } = {},
): string {
    // Quem decide a marcação é a ORIGEM: ver o comentário de `PreviewTemplate.origem`.
    const marcar = (texto: string, secao: 'header' | 'body') => preview.origem === 'WHIZ'
        ? destacarVariaveisDoBot(texto)
        : interpolarComDestaque(texto, params, secao);

    // Anexo sem legenda tem corpo VAZIO e ainda assim é um template escolhido: o
    // texto de estado vazio só vale quando não há nada — nem corpo, nem cabeçalho,
    // nem botão. Antes, todo template de anexo sem legenda dizia "nenhum template
    // selecionado" com o arquivo logo acima.
    const vazio = !preview.bodyText && !preview.headerType && !preview.botoes.length;
    const corpo = vazio
        ? '<i class="tpl-vazio">Nenhum template selecionado</i>'
        : marcar(preview.bodyText, 'body');

    let cabecalho = '';
    if (preview.headerType === 'text') {
        cabecalho = `<h6 class="tpl-header">${marcar(preview.headerText, 'header')}</h6>`;
    } else if (preview.headerType === 'file') {
        if (preview.headerFileMime === 'JPG' && preview.headerFileUrl) {
            cabecalho = `<div class="tpl-midia"><img src="${escaparHtml(preview.headerFileUrl)}" alt="" draggable="false"></div>`;
        } else if (preview.headerFileMime === 'MP4' && preview.headerFileUrl) {
            cabecalho = `<div class="tpl-midia"><video src="${escaparHtml(preview.headerFileUrl)}" preload="metadata" muted></video><i class="ri-play-circle-fill tpl-midia-play"></i></div>`;
        } else {
            cabecalho = `<div class="tpl-arquivo"><i class="ri-file-text-fill"></i><span>${escaparHtml(preview.headerFileName || 'Documento')}</span></div>`;
        }
    }

    const rodape = preview.footerText
        ? `<p class="tpl-footer">${escaparHtml(preview.footerText)}</p>`
        : '';

    // Os respondíveis vêm primeiro e na ordem do `buttonsJson`: a enésima porta do
    // Drawflow é o enésimo botão respondível, e qualquer reordenação aqui faria a
    // linha do fluxo apontar para o botão errado.
    const respondiveis = preview.botoes.filter(b => b.respondivel);
    const inertes = preview.botoes.filter(b => !b.respondivel);

    const linhaBotao = (b: BotaoPreview, classe: string, titulo: string) =>
        `<div class="tpl-btn ${classe}" title="${escaparHtml(titulo)}"><i class="${b.icon}"></i><span>${escaparHtml(b.texto)}</span></div>`;

    const botoes = preview.botoes.length
        ? `<div class="tpl-botoes${opcoes.comPortas ? ' tpl-botoes--portas' : ''}">
            ${respondiveis.map(b => linhaBotao(b, 'tpl-btn--resposta', `Resposta rápida "${b.texto}" — tem saída própria no fluxo.`)).join('')}
            ${inertes.map(b => linhaBotao(b, 'tpl-btn--inerte', `Este botão age no aparelho do contato e não devolve resposta ao fluxo.`)).join('')}
        </div>`
        : '';

    // `semSelo` para quem já desenha o selo por fora (o editor da ação o mostra ao
    // lado do título "Pré-visualização"); sem isto a categoria aparecia duas vezes.
    const selo = opcoes.semSelo ? null : seloDaCategoria(preview.categoria);
    const htmlSelo = selo
        ? `<span class="status-badge bdg-tone-${selo.tone} tpl-categoria" title="${escaparHtml(selo.descricao)}"><i class="${selo.icon} status-badge__icon"></i><span>${escaparHtml(selo.label)}</span></span>`
        : '';

    return `${htmlSelo}
        <div class="tpl-bolha">
            ${cabecalho}
            ${corpo ? `<p class="tpl-corpo">${corpo}</p>` : ''}
            ${rodape}
            ${botoes}
        </div>`;
}
